import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Info, Server } from 'lucide-react';
import { useAttachVolume } from '@/service/gpu-instance/volume-hook';
import { useListSSHInstances } from '@/service/gpu-instance/hook';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { SSHInstanceStatus } from '@/service/gpu-instance/type';
import { toast } from 'sonner';

interface AttachVolumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

export const AttachVolumeDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: AttachVolumeDialogProps) => {
  const [instanceId, setInstanceId] = useState('');
  const [mountPath, setMountPath] = useState('/data');

  const attachVolume = useAttachVolume();
  const { data: instances = [], isLoading: loadingInstances } = useListSSHInstances();

  // Filter to running instances only
  const availableInstances = instances.filter(
    (inst) => inst.status === SSHInstanceStatus.SSHReady || inst.status === SSHInstanceStatus.Running
  );

  if (!volume) return null;

  const validateMountPath = (path: string) => {
    return /^\/[a-zA-Z0-9_\-/]*$/.test(path) && !path.includes('//');
  };

  const isValid = instanceId && mountPath && validateMountPath(mountPath);

  const handleAttach = async () => {
    if (!isValid) return;

    try {
      await attachVolume.mutateAsync({
        volumeId: volume.id,
        instanceId,
        mountPath,
      });
      toast.success('Volume attached successfully', {
        description: `Volume "${volume.name}" is now attached at ${mountPath}.`,
      });
      onOpenChange(false);
      onSuccess?.();
      // Reset form
      setInstanceId('');
      setMountPath('/data');
    } catch {
      toast.error('Failed to attach volume');
    }
  };

  const accessModeHint = {
    ReadWriteOnce: 'Can only be mounted by one node',
    ReadWriteMany: 'Can be mounted by multiple nodes',
    ReadOnlyMany: 'Can be mounted read-only by multiple nodes',
    ReadWriteOncePod: 'Can only be mounted by one pod',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Attach Volume</DialogTitle>
          <DialogDescription>
            Attach "{volume.name}" to a GPU instance.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Volume Info */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Volume</span>
              <span className="font-medium">{volume.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span>{volume.requestedSize} GB</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Access Mode</span>
              <span>{volume.accessMode}</span>
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              {accessModeHint[volume.accessMode]}
            </AlertDescription>
          </Alert>

          {/* Instance Selection */}
          <div className="space-y-2">
            <Label htmlFor="instance">Target Instance</Label>
            {loadingInstances ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading instances...
              </div>
            ) : availableInstances.length === 0 ? (
              <Alert>
                <Server className="h-4 w-4" />
                <AlertDescription>
                  No running instances available. Start an instance first.
                </AlertDescription>
              </Alert>
            ) : (
              <Select value={instanceId} onValueChange={setInstanceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an instance" />
                </SelectTrigger>
                <SelectContent>
                  {availableInstances.map((inst) => (
                    <SelectItem key={inst.task_id} value={inst.task_id}>
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        <span>{inst.task_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Mount Path */}
          <div className="space-y-2">
            <Label htmlFor="mount-path">Mount Path</Label>
            <Input
              id="mount-path"
              placeholder="/data"
              value={mountPath}
              onChange={(e) => setMountPath(e.target.value)}
              className={mountPath && !validateMountPath(mountPath) ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              The path where the volume will be mounted inside the instance.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAttach} disabled={!isValid || attachVolume.isPending}>
            {attachVolume.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Attach Volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttachVolumeDialog;
